
import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Проверка обязательных полей
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          interest: formData.interest,
          message: formData.message
        })
      });

      if (!response.ok) {
        throw new Error('Request failed');
      }

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        interest: '',
        message: ''
      });

    } catch (error) {
      console.error(error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-6 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Side - Contact Info */}
          <div>
            <h2 className="text-[#00889D] text-4xl font-bold mb-8">Contact Us</h2>
            <p className="text-gray-700 text-base mb-8 leading-relaxed">
              Ready to begin your wellness journey? We’re here to help you find the perfect class and answer any questions
            </p>

            <div className="space-y-6 mb-8">
              <div>
                <h3 className="text-[#00889D] font-semibold mb-2">Studio Location</h3>
                <p className="text-gray-700 text-base">
                  105-19925 Willowbrook Dr., Langley<br />
                  Located near Willowbrook Shopping Centre
                </p>
              </div>

              <div>
                <h3 className="text-[#00889D] font-semibold mb-2">Phone</h3>
                <p className="text-gray-700 text-base">(778) 237-7790</p>
              </div>

              <div>
                <h3 className="text-[#00889D] font-semibold mb-2">Email</h3>
                <p className="text-gray-700 text-base">info@yogaseven.com</p>
              </div>

            </div>

            <div>
              <h3 className="text-[#00889D] font-semibold mb-4">Follow Us</h3>
              <div className="flex space-x-4">
                <a href="https://www.instagram.com/yogasevenstudio?igsh=MWtsY3lhd2piZW5hcg%3D%3D&utm_source=qr" className="w-10 h-10 bg-[#14B6A4] rounded-full flex items-center justify-center text-white hover:bg-[#0d9488] transition-colors cursor-pointer">
                  <i className="ri-instagram-line"></i>
                </a>
                <a href="https://www.facebook.com/profile.php?id=61585312964707" className="w-10 h-10 bg-[#14B6A4] rounded-full flex items-center justify-center text-white hover:bg-[#0d9488] transition-colors cursor-pointer">
                  <i className="ri-facebook-line"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Right Side - Contact Form */}
          <div>
            <h3 className="text-[#00889D] text-2xl font-bold mb-6">Send a Message</h3>

            <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-2 text-[#00889D]">
                    Full name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00889D] focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2 text-[#00889D]">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00889D] focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-[#00889D] mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00889D] focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="interest" className="block text-sm font-medium text-[#00889D] mb-2">
                    I'm interested in
                  </label>
                  <input
                    type="text"
                    id="interest"
                    name="interest"
                    value={formData.interest}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00889D] focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-[#00889D] mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00889D] focus:border-transparent text-sm resize-vertical"
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#00889D] hover:bg-[#006d7f] text-white px-6 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {isSubmitting ? 'Sending...' : 'Send'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-100 border border-green-300 rounded-lg">
                  <p className="text-green-700 text-sm">
                    Thank you! Your message has been sent successfully.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-100 border border-red-300 rounded-lg">
                  <p className="text-red-700 text-sm">
                    Sorry, there was an error. Please try again.
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
